/**
 * localStorage keys are part of the app's public surface in everything but
 * name: they are what makes a reload keep you in your session, keep your
 * language and keep your trailer choice.
 *
 * The rename from the old brand to FilmSloth would have orphaned all three at
 * once — every open session dropped, every language choice reset, every
 * trailer consent asked again — on the single deploy that changed the strings.
 * So the old key is read once and its value carried over to the new one.
 *
 * This is deliberately a one-way move: the legacy key is removed after it is
 * carried, so a browser pays the migration exactly once. Once enough time has
 * passed that no browser can still hold the old keys, `LEGACY_PREFIX` and the
 * body of the try block can go, and this becomes plain string concatenation.
 */
const LEGACY_PREFIX = 'what2watch.';
const PREFIX = 'filmsloth.';

export function brandedKey(suffix: string): string {
  const key = PREFIX + suffix;
  // Guarded rather than assumed: this runs at module scope, and a test
  // environment without a DOM would otherwise throw before any test does.
  if (typeof window === 'undefined') return key;
  try {
    const legacy = window.localStorage.getItem(LEGACY_PREFIX + suffix);
    if (legacy !== null) {
      // An existing new-format value wins — it is the more recent of the two.
      if (window.localStorage.getItem(key) === null) {
        window.localStorage.setItem(key, legacy);
      }
      window.localStorage.removeItem(LEGACY_PREFIX + suffix);
    }
  } catch {
    // Private mode, a full quota or blocked storage: the new key starts empty,
    // which is the same state a first-time visitor is in.
  }
  return key;
}
